import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Address CRUD page in a tab.
 */
const ADDRESS_CRUD_ROUTE = "/home/addresses/addressConfigCrud";

//endregion

//region Component

/**
 * Renders the Addresses configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Addresses MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - support an external refresh controller when the parent provides one;
 * - keep the selected address synchronized with the parent when required.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {Function} props.setSelectedAddress - Stores the selected address in the parent.
 * @param {boolean} props.refresh - Optional external refresh flag.
 * @param {Function} props.setRefresh - Optional external refresh setter.
 * @returns {JSX.Element} Addresses configuration page.
 */
export default function AddressConfig({
  roles,
  onCreateTab,
  toastMessages,
  setSelectedAddress,
  refresh: externalRefresh,
  setRefresh: setExternalRefresh,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [internalRefresh, setInternalRefresh] = useState(false);

  /**
   * Uses the parent refresh controller when available.
   *
   * Purpose:
   * - allows this config to work inside parent pages that own refresh state;
   * - still keeps the component self-contained when rendered standalone.
   */
  const refresh = externalRefresh ?? internalRefresh;
  const setRefresh = setExternalRefresh ?? setInternalRefresh;

  /**
   * Removes table actions that are not required in this configuration page.
   *
   * Important:
   * - Print is not part of the desired Addresses workflow;
   * - Copy is disabled to keep address creation explicit.
   */
  const removeContextItems = useMemo(() => ["Print", "Copy"], []);

  /**
   * Opens the Address CRUD tab for the requested operation.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View/Edit/Copy;
   * - persists the selected address row when required;
   * - optionally synchronizes the selected address with the parent;
   * - opens the configured CRUD route with a contextual tab label.
   *
   * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
   * @param {object|null|undefined} row - Selected address row.
   * @returns {void}
   */
  const openAddressCrud = useCallback(
    (operation, row) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (["Edit", "Copy", "View"].includes(operation)) {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "AddressLine",
            value: row,
          })
        );

        setSelectedAddress?.(row);
      }

      const tabLabel =
        operation === "Create"
          ? t("LabelNewAddress")
          : operation === "View"
            ? `${t("LabelViewAddress")} "${row?.Name ?? ""}"`
            : `${t("LabelEditAddress")} "${row?.Name ?? ""}"`;

      onCreateTab(ADDRESS_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, setSelectedAddress, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the tab-based Address workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openAddressCrud("View", row)],
        ["Create", () => openAddressCrud("Create")],
        ["Edit", (row) => openAddressCrud("Edit", row)],
      ]),
    [openAddressCrud]
  );

  return (
    <div
      className="config-page"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header">
        <div className="config-page__title">
          {t("Addresses")}
        </div>

        <div className="config-page__description">
          {t("InfoAddresses")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="Addresses"
          roleName="Accounts"
          rolePermissions={roles?.map((role) => role.Value)}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          replaceFunctions={replaceFunctions}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

AddressConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
  selectedAddress: PropTypes.object,
  setSelectedAddress: PropTypes.func,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
};

//endregion